import { captureException } from "@sentry/react"
import { Button, Divider, Tooltip, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { Product } from "api/resources/products/types"
import clsx from "clsx"
import AsyncBuilder from "components/AsyncBuilder"
import Card from "components/Card"
import ErrorPage from "components/ErrorPage"
import FullPageLoading from "components/FullPageLoading"
import PageContainer from "components/PageContainer"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import routes from "routes"
import { getPlatformURL } from "utils/helpers"
import useFormatPrice from "utils/hooks/useFormatPrice"

import AddAddress from "../CartlessPaymentFlow/AddAddress"
import AlreadyPaidScreen from "./AlreadyPaidScreen"
import {
  getLogoSrc,
  getSubscriptionRenewDurationType,
  SubscriptionSlugs,
  SubscriptionType,
} from "./helper"
import PaymentUnderProcess from "./PaymentUnderProcess"
import { useSubscriptionPayment } from "./useSubscriptionPayment"

export type Price = {
  currency: {
    code: string
    symbol: string
  }
  itemPrice: number
  finalAmount: number
  discountAmount: number

  taxes: { amount: number; name: string }[]
}

export const getPurchaseDetails = (
  product: Pick<Product, "name"> | undefined,
  price: Pick<Price, "finalAmount" | "currency"> | undefined | null
) => {
  if (!product || !price) return null

  const details = {
    itemName: product.name,
    amount: price.finalAmount,
    currency: price.currency,
  }

  return details
}

const CartlessSubscriptionFlow = () => {
  const [billingAddressAdded, setBillingAddressAdded] = useState(false)
  const [price, setPrice] = useState<Price | null>()

  const [searchParams] = useSearchParams()

  const productSlug = searchParams.get("product") as SubscriptionSlugs

  const [paymentUnderProcess, setPaymentUnderProcess] = useState<boolean>(false)

  const product = useQuery({
    queryFn: () => {
      if (!productSlug) throw new Error("productSlug is required")

      return api.products.getProduct({
        params: {
          fields: ["prices"],
        },
        urlParams: { productSlug },
      })
    },
    queryKey: ["product", productSlug],
    enabled: !!productSlug,
  })

  useEffect(() => {
    if (product.isSuccess) {
      if (product.data.prices == null) {
        captureException(
          new Error("Product prices is null in cartless subscription flow"),
          scope => {
            scope.setExtras({
              productData: product.data,
            })
            return scope
          }
        )
        return
      }
      setPrice({
        currency: product.data.prices.currency,
        itemPrice: product.data.prices.price,
        finalAmount: product.data.prices.price,
        discountAmount: 0,
        taxes: [],
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.isSuccess])

  const logoSrc = getLogoSrc(productSlug)

  const payment = useSubscriptionPayment({
    purchaseDetails: getPurchaseDetails(product.data, price),
    logoSrc,
    stripeConfig: {
      redirectRoute: routes.home,
    },
    razorpayConfig: {
      onSuccess: () => setPaymentUnderProcess(true),
      getUserDetails: () => ({}),
    },
  })

  const formatPrice = useFormatPrice()

  const { refreshSubscriptionData, isActive } = useSuraasaPlusContext()

  useQuery({
    queryKey: ["subscription-status-check"],
    enabled: paymentUnderProcess,
    refetchInterval: 3000,
    queryFn: async () => {
      // By default, react query stops polling if API returns an error.
      // Wrapping in try catch so that in case of any error the polling does not stop.
      try {
        await refreshSubscriptionData()
      } catch (error) {
        console.error(error)
      }
    },
  })

  const priceToPay = price
    ? `${price.currency.symbol}${formatPrice(price.finalAmount)}`
    : ""

  const canProceed = billingAddressAdded && payment.orderId != null

  useEffect(() => {
    setPaymentUnderProcess(false)
  }, [isActive])

  if (!productSlug || product.isError) {
    return (
      <ErrorPage
        title="Product not found"
        description="Please ensure that the link is correct."
        errorStatus={404}
      />
    )
  }

  if (product.isLoading) {
    return <FullPageLoading debugMessage="Product loading" />
  }

  if (isActive && product.isSuccess) {
    return <AlreadyPaidScreen productSlug={productSlug} />
  }

  if (paymentUnderProcess) {
    return (
      <PaymentUnderProcess
        onComplete={() => {
          setPaymentUnderProcess(false)
        }}
      />
    )
  }

  return (
    <div>
      {/* Stripe Checkout Form */}
      {payment.stripePaymentElement}

      <PageContainer>
        <Typography variant="title2" className="my-3">
          Complete Payment
        </Typography>

        <div className="mb-2 flex w-full flex-col md:flex-row">
          <div className="md:me-3 md:w-1/2">
            <Card padding={2} className="rounded-xl">
              <div className="flex grow flex-row sm:items-center">
                <img
                  alt="item"
                  src={logoSrc}
                  className="me-2 h-10 max-w-15 shrink-0 rounded-xl border border-surface-100 object-cover"
                />
                <div>
                  <Typography variant="strong">{product.data.name}</Typography>
                  <Typography variant="title4" className="mt-1">
                    {priceToPay}{" "}
                    {getSubscriptionRenewDurationType(productSlug) ===
                      SubscriptionType.monthly && (
                      <span className="font-medium text-onSurface-500">
                        per month
                      </span>
                    )}
                    {getSubscriptionRenewDurationType(productSlug) ===
                      SubscriptionType.yearly && (
                      <span className="font-medium text-onSurface-500">
                        per year
                      </span>
                    )}
                  </Typography>
                </div>
              </div>
            </Card>

            <AddAddress
              onAdd={async addr => {
                const res = await payment.createOrder({
                  billingAddressId: addr.uuid,
                  productSlug: productSlug,
                  isUserless: false,
                })

                const order = res ? ("order" in res ? res.order : res) : null

                if (order) {
                  setPrice({
                    itemPrice: order.itemTotal,
                    finalAmount: order.finalAmount,
                    discountAmount: order.discountAmount,
                    taxes: order.taxes,
                    currency: order.currency,
                  })
                  setBillingAddressAdded(true)
                }
              }}
              onChangeClicked={() => {
                setBillingAddressAdded(false)
              }}
            />
          </div>
          <div className="mb-10 mt-3 flex flex-col md:mt-0 md:w-1/2">
            {price && (
              <div className="rounded-2xl border border-surface-200 bg-[#F7F7F7] px-2 py-1.25">
                {[
                  { name: "Item", amount: price.itemPrice },
                  {
                    name: "Discount",
                    amount: price.discountAmount,
                  },
                  {
                    name: "Taxes",
                    amount: price.taxes?.reduce((a, b) => a + b.amount, 0),
                  },
                ]
                  .filter(item => item.amount > 0)
                  .map((item, idx) => (
                    <div
                      key={idx}
                      className="mb-2 flex items-center justify-between"
                    >
                      <Typography variant="strong">{item.name}</Typography>
                      <Typography
                        variant="strong"
                        className={clsx({
                          "text-success-500": item.name === "Discount",
                          "text-onSurface-900": item.name !== "Discount",
                        })}
                      >
                        {item.name === "Discount" && "-"}
                        {price.currency.symbol}
                        {formatPrice(item.amount)}
                      </Typography>
                    </div>
                  ))}
                <Divider weight="light" className="bg-onSurface-200" />
                <span className="mt-1 flex items-center justify-between">
                  <span className="flex items-center">
                    <Typography variant="strong" className="me-0.5">
                      Total
                    </Typography>
                  </span>
                  <Typography variant="title2">{priceToPay}</Typography>
                </span>
              </div>
            )}
            <Tooltip
              content="Please add a billing address first"
              enabled={!canProceed}
            >
              <div>
                <AsyncBuilder
                  handler={async () => {
                    await payment.checkout()
                  }}
                  render={({ loading, onClick }) => (
                    <Button
                      color="black"
                      loading={loading}
                      onClick={onClick}
                      variant="filled"
                      className="mt-2 w-full"
                      disabled={!canProceed}
                    >
                      Pay {priceToPay}
                    </Button>
                  )}
                />
              </div>
            </Tooltip>
            <Typography
              variant="smallBody"
              className="mt-1 w-4/5 self-center text-center text-secondary-500"
            >
              This transaction is non-refundable. By proceeding you agree to
              accept our
              <a
                className="text-interactive-500"
                href={getPlatformURL("suraasa", "/terms-of-use")}
                target="_blank"
                rel="noreferrer"
              >
                {" "}
                terms & conditions
              </a>{" "}
            </Typography>
          </div>
        </div>
      </PageContainer>
    </div>
  )
}

export default CartlessSubscriptionFlow
